import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './Auth.css';

function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp, resetPassword, updatePassword } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user && !isRecovery) { navigate('/workspace'); }
  }, [user, navigate, isRecovery]);

  useEffect(() => {
    if (searchParams.get('type') === 'recovery') { setIsRecovery(true); }
  }, [searchParams]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRecovery) {
        if (formData.password !== formData.confirmPassword) { setError('两次输入的密码不一致'); setLoading(false); return; }
        if (formData.password.length < 6) { setError('密码长度至少为6位'); setLoading(false); return; }
        await updatePassword(formData.password);
        setIsRecovery(false); setError('');
        alert('密码已重置，请重新登录');
        setIsLogin(true); setFormData({ email: '', password: '', confirmPassword: '' }); navigate('/auth');
      } else if (forgotPassword) {
        await resetPassword(formData.email);
        setResetSent(true);
      } else if (isLogin) {
        await signIn(formData.email, formData.password);
        navigate('/workspace');
      } else {
        if (formData.password !== formData.confirmPassword) { setError('两次输入的密码不一致'); setLoading(false); return; }
        if (formData.password.length < 6) { setError('密码长度至少为6位'); setLoading(false); return; }
        await signUp(formData.email, formData.password);
        setRegisterSuccess(true); setError('');
      }
    } catch (err) {
      console.error('认证错误:', err);
      if (err.message.includes('Invalid login credentials')) { setError('邮箱或密码错误'); }
      else if (err.message.includes('User already registered')) { setError('该邮箱已被注册'); }
      else if (err.message.includes('Email not confirmed')) { setError('请先验证邮箱后再登录'); }
      else if (err.message.includes('Password should contain')) { setError('密码需包含大写字母、小写字母和数字'); }
      else if (err.message.includes('For security purposes')) { setError('操作过于频繁，请稍后再试'); }
      else { setError(err.message || '操作失败，请重试'); }
    } finally { setLoading(false); }
  };

  return (
    <div className="at-page">
      <div className="at-container">
        <div className="at-top-actions">
          <button onClick={() => navigate('/')} className="at-back">← 返回首页</button>
          <div className="theme-toggle" onClick={toggleTheme} title="切换主题">
            <span className={`theme-toggle-label theme-toggle-label--light ${theme === 'light' ? 'theme-toggle-label--active' : ''}`}>浅色</span>
            <span className={`theme-toggle-label theme-toggle-label--dark ${theme === 'dark' ? 'theme-toggle-label--active' : ''}`}>深色</span>
            <span className={`theme-toggle-indicator ${theme === 'dark' ? 'theme-toggle-indicator--dark' : ''}`} />
          </div>
        </div>

        <div className="at-card">
          <div className="at-card-bar">
            <span className="at-dot at-dot--r" />
            <span className="at-dot at-dot--y" />
            <span className="at-dot at-dot--g" />
            <span className="at-card-title">
              {registerSuccess ? '注册成功' : resetSent ? '邮件已发送' : isRecovery ? '重置密码' : forgotPassword ? '找回密码' : '认证'}
            </span>
          </div>
          <div className="at-card-body">
          {registerSuccess ? (
            <div className="at-success">
              <div className="at-success-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="22" stroke="#22c55e" strokeWidth="3"/>
                  <path d="M15 24L21 30L33 18" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="at-success-title">注册成功</h2>
              <p className="at-success-desc">验证邮件已发送至 <strong>{formData.email}</strong></p>
              <div className="at-success-hint">
                请查看收件箱（或垃圾邮件），点击验证链接完成注册。
                <br />验证通过后将获赠 <strong>¥1.00</strong> 欢迎余额。
              </div>
              <button className="at-submit" onClick={() => { setRegisterSuccess(false); setIsLogin(true); setFormData({ email: formData.email, password: '', confirmPassword: '' }); }}>
                去登录
              </button>
            </div>
          ) : resetSent ? (
            <div className="at-success">
              <div className="at-success-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="22" stroke="#22c55e" strokeWidth="3"/>
                  <path d="M15 24L21 30L33 18" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="at-success-title">邮件已发送</h2>
              <p className="at-success-desc">重置密码链接已发送至 <strong>{formData.email}</strong></p>
              <div className="at-success-hint">请查看收件箱（或垃圾邮件），点击链接重置密码。</div>
              <button className="at-submit" onClick={() => { setResetSent(false); setForgotPassword(false); setIsLogin(true); }}>
                返回登录
              </button>
            </div>
          ) : isRecovery ? (
            <>
            <div className="at-header">
              <h1 className="at-logo">AIZIMU</h1>
              <p className="at-tagline">设置新密码</p>
            </div>
            <form onSubmit={handleSubmit} className="at-form">
              {error && <div className="at-error">{error}</div>}
              <div className="at-field">
                <label>新密码</label>
                <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="需含大小写字母和数字，至少6位" required disabled={loading} />
              </div>
              <div className="at-field">
                <label>确认新密码</label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} placeholder="请再次输入新密码" required disabled={loading} />
              </div>
              <button type="submit" className="at-submit" disabled={loading}>{loading ? '提交中...' : '重置密码'}</button>
            </form>
            </>
          ) : (
            <>
            <div className="at-header">
              <h1 className="at-logo">AIZIMU</h1>
              <p className="at-tagline">中文方言字幕专家</p>
            </div>

            {forgotPassword ? (
              <>
              <form onSubmit={handleSubmit} className="at-form">
                {error && <div className="at-error">{error}</div>}
                <p className="at-hint">输入注册邮箱，我们将发送密码重置链接</p>
                <div className="at-field">
                  <label>邮箱</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="请输入注册邮箱" required disabled={loading} />
                </div>
                <button type="submit" className="at-submit" disabled={loading}>{loading ? '发送中...' : '发送重置链接'}</button>
                <p className="at-link"><a href="#back" onClick={(e) => { e.preventDefault(); setForgotPassword(false); setError(''); }}>返回登录</a></p>
              </form>
              </>
            ) : (
              <>
              <div className="at-tabs">
                <button className={`at-tab ${isLogin ? 'at-tab--active' : ''}`} onClick={() => setIsLogin(true)}>登录</button>
                <button className={`at-tab ${!isLogin ? 'at-tab--active' : ''}`} onClick={() => setIsLogin(false)}>注册</button>
                <div className={`at-tab-indicator ${!isLogin ? 'at-tab-indicator--right' : ''}`} />
              </div>

              <form onSubmit={handleSubmit} className="at-form">
                {error && <div className="at-error">{error}</div>}
                <div className="at-field">
                  <label>邮箱</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="请输入邮箱地址" required disabled={loading} />
                </div>
                <div className="at-field">
                  <label>密码</label>
                  <input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder={isLogin ? '请输入密码' : '需含大小写字母和数字，至少6位'} required disabled={loading} />
                </div>
                {!isLogin && (
                  <div className="at-field">
                    <label>确认密码</label>
                    <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} placeholder="请再次输入密码" required={!isLogin} disabled={loading} />
                  </div>
                )}
                <button type="submit" className="at-submit" disabled={loading}>
                  {loading ? (isLogin ? '登录中...' : '注册中...') : (isLogin ? '登录' : '注册')}
                </button>
                {isLogin && (
                  <p className="at-link"><a href="#forgot" onClick={(e) => { e.preventDefault(); setForgotPassword(true); setError(''); }}>忘记密码？</a></p>
                )}
                {!isLogin && (
                  <p className="at-link">注册即表示您同意我们的<a href="#terms">服务条款</a>和<a href="#privacy">隐私政策</a></p>
                )}
              </form>
              </>
            )}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Auth;
